import { ref } from 'vue'
import getEnvironment from '@/utils/env'
import { useScriptTag } from '@vueuse/core'
import i18n from './i18n'

declare global {
  interface Window {
    OptanonWrapper?: () => void
    readonly OnetrustActiveGroups?: string
    readonly OneTrust?: {
      ToggleInfoDisplay: () => void
      GetDomainData: () => unknown
      changeLanguage: (language: string) => void
    }
  }
}

// OneTrust cookie category IDs
// See: https://app-eu.onetrust.com/cookies/categories for category configuration
export const COOKIE_CATEGORIES = {
  essential: 'C0001',
  analytics: 'C0002',
  functional: 'C0003',
  marketing: 'C0004',
  socialMedia: 'C0005',
} as const

export type CookieCategory = keyof typeof COOKIE_CATEGORIES

interface ConsentCallback {
  onAccepted?: () => void
  onDenied?: () => void
}

// Registry of callbacks per category
const consentListeners = new Map<CookieCategory, ConsentCallback[]>()

// Track initialization state
let isInitialized = false

// Reactive flag — true only when the OneTrust SDK has actually loaded and
// `window.OneTrust` is callable. Stays false in localhost (where the scripts
// are skipped entirely) so consumers can `v-if` UI like the Cookies button.
export const isOneTrustReady = ref(false)

export function openCookieSettings(): void {
  window.OneTrust?.ToggleInfoDisplay()
}

/**
 * Register callbacks for when consent status changes for a category.
 * Callbacks are invoked immediately if OneTrust is already initialized,
 * and again whenever the user updates their preferences.
 */
export function onConsentChange(category: CookieCategory, callbacks: ConsentCallback): () => void {
  const listeners = consentListeners.get(category) ?? []
  listeners.push(callbacks)
  consentListeners.set(category, listeners)

  // If already initialized, invoke callback immediately based on current consent
  if (isInitialized) {
    invokeCallback(category, callbacks)
  }

  // Return unsubscribe function
  return () => {
    const current = consentListeners.get(category) ?? []
    const idx = current.indexOf(callbacks)
    if (idx !== -1) {
      current.splice(idx, 1)
    }
  }
}

/**
 * Check if consent has been granted for a specific category.
 */
export function hasConsent(category: CookieCategory): boolean {
  const categoryId = COOKIE_CATEGORIES[category]
  const activeGroups = (window.OnetrustActiveGroups ?? '').split(',')
  return activeGroups.includes(categoryId)
}

/**
 * Initialize OneTrust cookie consent.
 * Should be called once during app bootstrap in main.ts.
 */
export function initOneTrust(): void {
  const environment = getEnvironment()
  const forceEnable = import.meta.env.VITE_TEST_EXTERNAL_SCRIPTS === 'true'

  if (!forceEnable && (environment == null || environment.id === 'localhost')) {
    // In development, mark as initialized and grant all consent by default
    isInitialized = true
    notifyAllListeners()
    return
  }

  const isProduction = environment?.id === 'production'
  const domainScript = `${import.meta.env.VITE_ONETRUST_DOMAIN_SCRIPT_ID}${isProduction ? '' : '-test'}`

  // Set up the OptanonWrapper callback that OneTrust invokes
  window.OptanonWrapper = () => {
    isInitialized = true
    isOneTrustReady.value = true
    notifyAllListeners()
  }

  // Load the OneTrust SDK stub (renders the consent banner). Note: the
  // OtAutoBlock.js companion script is intentionally NOT loaded — it is not
  // published for our domain script (404s for both prod and -test variants;
  // the legacy app silently made the same failing request). Script blocking
  // is handled by the consent callback registry in this module instead.
  useScriptTag('https://cdn.cookielaw.org/scripttemplates/otSDKStub.js', undefined, {
    attrs: {
      'data-domain-script': domainScript,
      'data-language': i18n.global.locale.value,
    },
  })
}

/**
 * Remove cookies by name. Attempts removal on multiple domain variations.
 */
export function removeCookies(cookieNames: string[]): void {
  const hostname = window.location.hostname
  const rootDomain = hostname.split('.').slice(-2).join('.')

  const domains = [`.${rootDomain}`, rootDomain, `.${hostname}`, hostname]

  for (const name of cookieNames) {
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`
    }
  }
}

// --- Internal helpers ---

function notifyAllListeners(): void {
  for (const [category, listeners] of consentListeners) {
    for (const callback of listeners) {
      try {
        invokeCallback(category, callback)
      } catch (err) {
        console.error(`Error invoking consent callback for ${category}:`, err)
      }
    }
  }
}

function invokeCallback(category: CookieCategory, callback: ConsentCallback): void {
  if (hasConsent(category)) {
    callback.onAccepted?.()
  } else {
    callback.onDenied?.()
  }
}
